import type { Middleware, MiddlewareCallbackParams } from 'openapi-fetch'

/** The `detail.status` values the backend uses for a revoked session (wire fixtures `error-401-*`). */
const REVOKED_STATUSES = new Set(['session_revoked', 'device_revoked', 'device_id_required'])

const FALLBACK_MESSAGE = 'You were signed out. Sign in again to continue.'

export type SessionRevokedMiddleware = Middleware & {
  onResponse: (
    params: Pick<MiddlewareCallbackParams & { response: Response }, 'response'>,
  ) => Promise<Response | undefined>
}

/**
 * The server's message for a 401 that means "this session is gone", else `null`.
 * Any other 401 — an expired token, say — is `null` and keeps its current handling.
 */
export function parseRevokedBody(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) return null
  const detail = (value as Record<string, unknown>).detail
  if (typeof detail !== 'object' || detail === null) return null
  const d = detail as Record<string, unknown>
  if (typeof d.status !== 'string' || !REVOKED_STATUSES.has(d.status)) return null
  return typeof d.message === 'string' && d.message.trim() !== '' ? d.message : FALLBACK_MESSAGE
}

/** Reads a clone, so the body `openapi-fetch` still has to read is untouched. */
export function createSessionRevokedMiddleware(
  onRevoked: (message: string) => void,
): SessionRevokedMiddleware {
  return {
    async onResponse({ response }) {
      if (response.status !== 401) return undefined
      let raw: unknown
      try {
        raw = await response.clone().json()
      } catch {
        return undefined
      }
      const message = parseRevokedBody(raw)
      if (message !== null) onRevoked(message)
      return undefined
    },
  }
}
